/* eslint-disable import/prefer-default-export */
import React from 'react';
import { render, unmountComponentAtNode } from 'react-dom';
import Roblox from 'Roblox';
import { RequestServiceDefault } from '../../../common/request';
import { ConsumerType } from '../../../common/request/types/translations';
import App from './App';
import { CAPTCHA_LANGUAGE_RESOURCES, LOG_PREFIX, TRANSLATION_CONFIG } from './app.config';
import { RenderChallenge } from './interface';

// Global instance since we do not need captcha parameters for instantiation.
const requestServiceDefault = new RequestServiceDefault();

// Export some additional enums that are declared in the shared interface (they
// are also defined in the shared interface, but we need to expose them in the
// object hierarchy for the challenge component).
export { ActionType, ErrorCode } from './interface';

/**
 * Renders the Captcha UI for a given set of parameters.
 * Returns whether the UI could be successfully rendered.
 */
export const renderChallenge: RenderChallenge = async ({
  containerId,
  actionType,
  appType,
  dataExchangeBlob,
  unifiedCaptchaId,
  renderInline,
  shouldDynamicallyLoadTranslationResources,
  onChallengeDisplayed,
  onChallengeCompleted,
  onChallengeInvalidated,
  onModalChallengeAbandoned
}) => {
  const container = document.getElementById(containerId);
  if (container !== null) {
    // Remove any existing instances of the app.
    unmountComponentAtNode(container);

    // Request language resources dynamically (so this component can use CDN).
    // Populate them on `Roblox.Lang` so we can use standard I18N tooling.
    if (shouldDynamicallyLoadTranslationResources) {
      const result = await requestServiceDefault.translations.getLanguageResources(
        ConsumerType.Web,
        TRANSLATION_CONFIG.feature,
        CAPTCHA_LANGUAGE_RESOURCES
      );
      if (result.isError) {
        // eslint-disable-next-line no-console
        console.error(
          LOG_PREFIX,
          `Got error code ${result.error?.toString() || 'null'} loading translations`
        );
        return false;
      }
      const RobloxUntyped = (Roblox as Record<string, unknown>) || {};
      RobloxUntyped.Lang = RobloxUntyped.Lang || {};
      Object.assign(RobloxUntyped.Lang, {
        [TRANSLATION_CONFIG.feature]: result.value
      });
    }

    // Render the app on the selected element.
    render(
      <App
        actionType={actionType}
        appType={appType}
        dataExchangeBlob={dataExchangeBlob}
        unifiedCaptchaId={unifiedCaptchaId}
        renderInline={renderInline}
        requestService={requestServiceDefault}
        onChallengeDisplayed={onChallengeDisplayed}
        onChallengeCompleted={onChallengeCompleted}
        onChallengeInvalidated={onChallengeInvalidated}
        onModalChallengeAbandoned={onModalChallengeAbandoned}
      />,
      container
    );
    return true;
  }

  // Return a Promise to better standardize our challenge interface.
  return Promise.resolve(false);
};
